import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { AppTrItem, AppTranslate } from "./AppTranslate";
import { AppTranslateBubble, ITrDoc, ITrNode } from "./AppTranslateBubble";
import { AppTranslateRules } from "./AppTranslateRules";
import { AppChatRules } from "./AppChatRules";
import { AppAssistCopyJob } from "./AppAssistTools";

// AppTranslateFrame - TRANSLATION'S BEHAVIOUR INSIDE THE OPEN CONVERSATION, attached from OUTSIDE the script-free
// `blob:` frame (like `AppReactions`): one delegated click listener on the frame's document.
//   - the flag (`button.trf`) shows / hides its bubble's original (`tron`);
//   - Copy (`button.trc`) copies the original's text (the Clipboard API, then `execCommand` - `AppAssistCopyJob`);
//     a refused clipboard is one ink sentence under the box;
//   - Retry / Send in English on a "Not sent - couldn't translate" bubble hand the bubble's words to the host,
//     which sends them through `AppComposer.SendText` (the ONE sender, ONE latch) as a NEW request;
//   - "Translate earlier messages" (a button at the top of the thread) asks the host for the next batch, and the
//     answer is patched into the bubbles by `data-mid` (`AppTranslateBubble.Patch`).
//
// ⛔ DECISION 9: NOTHING HERE SENDS BY ITSELF. Retry and Send in English run only on the owner's click on that button,
//    each a new request; the flag, Copy, a page load and an arriving answer never reach the host's `Resend`.
// ⛔ The archive page's own standalone script never runs here: `AppMain.Frame` removed it (`AppPageScript.Strip`)
//    before the frame was built, and the app's CSP allows no inline script anyway.
// ⚠ SHAPED BY INTERFACES, so the suite drives it with a fake document, copier and host.

/** A DOM event's target, as far as this reads it. */
export interface ITrTarget {
    closest(Selector: string): ITrNode | null;
}

export interface ITrFrameDoc extends ITrDoc {
    addEventListener(Type: string, Listener: (Ev: Event) => void): void;
}

export interface ITrFrame {
    contentDocument: ITrFrameDoc | null;
    addEventListener(Type: string, Listener: () => void): void;
}

/** How one copy went. */
export interface IAppTrCopied {
    Copied(): void;
    Refused(): void;
}

/** The clipboard. */
export interface IAppTrCopier {
    Copy(Text: string, Then: IAppTrCopied): void;
}

/** How one Retry / Send in English went (the composer's `IComposerSent` fits by shape). */
export interface IAppTrResent {
    Queued(): void;
    Failed(Why: string): void;
}

/** The page's side. ⛔ `Resend` is `AppComposer.SendText` with the translate choice fixed - reached only from a click. */
export interface IAppTrFrameHost {
    /** "" when the request is on its way (`Then` hears once, later), else the sentence to show. */
    Resend(Text: string, Translate: boolean, Then: IAppTrResent): string;
    /** "Translate earlier messages" was pressed. */
    AskEarlier(): void;
    /** The page is in (the host may now offer the earlier link, which needs to see the bubbles). */
    Loaded(): void;
    /** A sentence under the box. */
    Say(Text: string, Bad: boolean): void;
}

/** The browser's clipboard, through the AI panel's copy job (Clipboard API, then `execCommand`). */
export class AppTrClipboard implements IAppTrCopier {
    public Copy = (Text: string, Then: IAppTrCopied): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            new AppAssistCopyJob(nz(Text), Then).Start();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTrClipboard.Copy");
        }
    };
}

export class AppTranslateFrame {
    public static readonly EarlierClass: string = "trearlier";
    public static readonly CopiedWords: string = "Copied";
    public static readonly CopyRefusedWhy: string = "Couldn't copy - select the text and copy it by hand.";

    private Host: IAppTrFrameHost;
    private Copier: IAppTrCopier;
    private Frame: ITrFrame | null = null;
    private Doc: ITrFrameDoc | null = null;
    private Stopped: boolean = false;
    /** Offer the earlier link once the page is in (set before or after the load). */
    private WantEarlier: boolean = false;

    public constructor(Host: IAppTrFrameHost, Copier: IAppTrCopier) {
        this.Host = Host;
        this.Copier = Copier;
    }

    public Attach = (Frame: ITrFrame): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Frame = Frame;
            Frame.addEventListener("load", this.OnLoad);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTranslateFrame.Attach");
        }
    };

    public OnLoad = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Stopped === true || this.Frame === null || this.Frame.contentDocument === null || this.Doc !== null) { return; }
            this.Doc = this.Frame.contentDocument;
            (this.Doc as ITrFrameDoc).addEventListener("click", this.OnClick);
            if (this.WantEarlier === true) { this.PaintEarlier(true, ""); }
            this.Host.Loaded();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTranslateFrame.OnLoad");
        }
    };

    /** The frame is being replaced: nothing more is drawn or answered.
     *  DOCUMENTED EXCEPTION: two field writes. ERROR-SUPPRESSED-JUSTIFIED: no gate - a stop must work whatever it says. */
    public Stop = (): void => {
        this.Stopped = true;
        this.Doc = null;
    };

    /** The loaded page (null before `load` / after `Stop`). DOCUMENTED EXCEPTION: a field read.
     *  ERROR-SUPPRESSED-JUSTIFIED: cannot throw. */
    public Page = (): ITrFrameDoc | null => {
        if (this.Stopped === true) { return null; }
        return this.Doc;
    };

    /** One click anywhere in the page: only our buttons mean anything. */
    public OnClick = (Ev: Event): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Stopped === true || Ev === null) { return; }
            const Target: ITrTarget | null = AppTranslateFrame.TargetOf(Ev);
            if (Target === null) { return; }
            const Flag: ITrNode | null = Target.closest("." + AppTranslate.FlagClass);
            if (Flag !== null) { this.OnFlag(Flag as ITrNode); return; }
            const Copy: ITrNode | null = Target.closest("." + AppTranslate.CopyClass);
            if (Copy !== null) { this.OnCopy(Copy as ITrNode); return; }
            const Act: ITrNode | null = Target.closest("[" + AppTranslateBubble.ActAttr + "]");
            if (Act !== null) { this.OnAct(Act as ITrNode); return; }
            if (Target.closest("." + AppTranslateFrame.EarlierClass) !== null) { this.Host.AskEarlier(); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTranslateFrame.OnClick");
        }
    };

    /** DOCUMENTED EXCEPTION: reads the event's target. ERROR-SUPPRESSED-JUSTIFIED: null (not ours) IS the answer. */
    private static TargetOf = (Ev: Event): ITrTarget | null => {
        const Raw: unknown = (Ev as unknown as Record<string, unknown>).target;
        if (Raw === null || typeof Raw !== "object") { return null; }
        if (typeof (Raw as Record<string, unknown>).closest !== "function") { return null; }
        return Raw as ITrTarget;
    };

    private OnFlag = (Flag: ITrNode): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Bubble: ITrNode | null = (Flag as unknown as ITrTarget).closest(".b");
            if (Bubble !== null) { AppTranslateBubble.Toggle(Bubble as ITrNode, Flag); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTranslateFrame.OnFlag");
        }
    };

    private OnCopy = (Copy: ITrNode): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Box: ITrNode | null = (Copy as unknown as ITrTarget).closest("." + AppTranslate.OriginalClass);
            if (Box === null) { return; }
            const Words: ITrNode | null = (Box as ITrNode).querySelector("." + AppTranslate.OriginalTextClass);
            if (Words === null) { return; }
            this.Copier.Copy(nz(Words.textContent), new AppTrCopyDone(this, Copy));
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTranslateFrame.OnCopy");
        }
    };

    /** The clipboard answered. Copied: the button says so for a moment. Refused: one ink sentence under the box. */
    public Copied = (Copy: ITrNode, Ok: boolean): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (Ok === true) { Copy.textContent = AppTranslateFrame.CopiedWords; return; }
            this.Host.Say(AppTranslateFrame.CopyRefusedWhy, false);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTranslateFrame.Copied");
        }
    };

    /** ⛔ Retry / Send in English - the owner's click, a NEW request through the host's one sender. */
    private OnAct = (Button: ITrNode): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Bubble: ITrNode | null = (Button as unknown as ITrTarget).closest(".b");
            if (Bubble === null) { return; }
            const Words: ITrNode | null = (Bubble as ITrNode).querySelector(".txt");
            if (Words === null) { return; }
            const Translate: boolean = Button.getAttribute(AppTranslateBubble.ActAttr) === "retry";
            const Why: string = this.Host.Resend(nz(Words.textContent), Translate, new AppTrResentOld(Bubble as ITrNode, Translate));
            if (Why.length > 0) { this.Host.Say(Why, true); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTranslateFrame.OnAct");
        }
    };

    /**
     * The owner's own language for this chat is known (or changed): the page is told, so a failed bubble's second
     * button names it (`AppTranslateBubble.NoteLang`). Before the page loads there is nothing to tell - the link
     * says it again from `Loaded`.
     */
    public NoteLang = (MyLang: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Doc !== null) { AppTranslateBubble.NoteLang(this.Doc as ITrFrameDoc, MyLang); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTranslateFrame.NoteLang");
        }
    };

    // ── Translate earlier messages ─────────────────────────────────────────────────────────────

    /** Offer (or withdraw) the link at the top of the thread. Before the page loads, remembered for the load. */
    public OfferEarlier = (Show: boolean): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.WantEarlier = Show;
            if (this.Doc !== null) { this.PaintEarlier(Show, ""); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTranslateFrame.OfferEarlier");
        }
    };

    /**
     * The link's state: `Busy` "" = ready to press ("Translate earlier messages"); otherwise its words while a batch
     * is being translated ("Translating earlier messages... 4 s" - ticking once a second, the house rule).
     */
    public PaintEarlier = (Show: boolean, Busy: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Doc === null) { return; }
            const Doc: ITrFrameDoc = this.Doc as ITrFrameDoc;
            let Link: ITrNode | null = Doc.querySelector("." + AppTranslateFrame.EarlierClass);
            if (Link === null && Show === false) { return; }
            if (Link === null) {
                const Chat: ITrNode | null = Doc.querySelector(".chat");
                if (Chat === null) { return; }
                Link = Doc.createElement("button");
                Link.setAttribute("type", "button");
                Link.className = AppTranslateFrame.EarlierClass;
                // ⚠ `.chat` is `column-reverse`: its LAST child is the TOP of the conversation.
                (Chat as ITrNode).appendChild(Link as ITrNode);
            }
            AppTranslateFrame.EarlierState(Link as ITrNode, Show, Busy);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTranslateFrame.PaintEarlier");
        }
    };

    /** DOCUMENTED EXCEPTION: sets three attributes. ERROR-SUPPRESSED-JUSTIFIED: the caller is guarded. */
    private static EarlierState = (Link: ITrNode, Show: boolean, Busy: string): void => {
        if (Show === false) { Link.setAttribute("hidden", ""); return; }
        Link.removeAttribute("hidden");
        if (Busy.length > 0) { Link.textContent = Busy; Link.setAttribute("disabled", ""); return; }
        Link.textContent = AppTranslateRules.EarlierLabel;
        Link.removeAttribute("disabled");
    };

    /** An `earlier` batch arrived: its translations go onto their bubbles. Answers how many changed. */
    public Patch = (Items: AppTrItem[]): number => {
        if (getCoreUtils().KErrorMode === true) { return 0; }
        try {
            if (this.Doc === null) { return 0; }
            return AppTranslateBubble.Patch(this.Doc as ITrFrameDoc, Items);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTranslateFrame.Patch");
            return 0;
        }
    };

    /** The UTC minutes (12 digits) of the translated bubbles on the page - `before` comes from the oldest. */
    public TranslatedMinutes = (): string[] => {
        if (getCoreUtils().KErrorMode === true) { return []; }
        try {
            const Out: string[] = [];
            if (this.Doc === null) { return Out; }
            const Found: ArrayLike<ITrNode> = (this.Doc as ITrFrameDoc).querySelectorAll("." + AppTranslate.BubbleClass);
            for (let At: number = 0; At < Found.length; At = At + 1) {
                const Minute: string = AppChatRules.MinuteOfId(nz(Found[At].getAttribute("id")));
                if (Minute.length > 0) { Out.push(Minute); }
            }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTranslateFrame.TranslatedMinutes");
            return [];
        }
    };

    /**
     * Does the page hold a message from the contact OLDER than its oldest translated one (or any, when nothing is
     * translated yet)? That is when "Translate earlier messages" has something to do without the service's `more`.
     */
    public OlderUntranslated = (): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            if (this.Doc === null) { return false; }
            let Oldest: string = "";
            for (const One of this.TranslatedMinutes()) { if (Oldest.length === 0 || One < Oldest) { Oldest = One; } }
            const Found: ArrayLike<ITrNode> = (this.Doc as ITrFrameDoc).querySelectorAll(".them");
            for (let At: number = 0; At < Found.length; At = At + 1) {
                if (AppTranslateBubble.HasClass(Found[At], AppTranslate.BubbleClass) === true) { continue; }
                const Minute: string = AppChatRules.MinuteOfId(nz(Found[At].getAttribute("id")));
                if (Minute.length > 0 && (Oldest.length === 0 || Minute < Oldest)) { return true; }
            }
            return false;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTranslateFrame.OlderUntranslated");
            return false;
        }
    };
}

/** One copy's answer, carried back to its button. A class, not a closure. */
export class AppTrCopyDone implements IAppTrCopied {
    private Owner: AppTranslateFrame;
    private Button: ITrNode;

    public constructor(Owner: AppTranslateFrame, Button: ITrNode) {
        this.Owner = Owner;
        this.Button = Button;
    }

    /** DOCUMENTED EXCEPTION: forwards to the guarded `AppTranslateFrame.Copied`. ERROR-SUPPRESSED-JUSTIFIED: one call. */
    public Copied = (): void => { this.Owner.Copied(this.Button, true); };
    /** DOCUMENTED EXCEPTION: forwards to the guarded `AppTranslateFrame.Copied`. ERROR-SUPPRESSED-JUSTIFIED: one call. */
    public Refused = (): void => { this.Owner.Copied(this.Button, false); };
}

/** The failed bubble hears how its Retry / Send in English went: once it went, it stops offering them. */
export class AppTrResentOld implements IAppTrResent {
    private Bubble: ITrNode;
    private Translate: boolean;

    public constructor(Bubble: ITrNode, Translate: boolean) {
        this.Bubble = Bubble;
        this.Translate = Translate;
    }

    public Queued = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            let Words: string = "Sent again below";
            if (this.Translate === false) { Words = "Sent untranslated below"; }
            AppTranslateBubble.Settled(this.Bubble, Words);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTrResentOld.Queued");
        }
    };

    /** The new request was refused before it went - the buttons stay, the page's hint says why. */
    public Failed = (Why: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (nz(Why).length === 0) { return; }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTrResentOld.Failed");
        }
    };
}
